import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { UtilityService } from '@eforall/common';
import { DialogService, GridContentWidget, PageTabsMainTabGridSize } from '@eforall/dashboard';
import { AccVentureFuncData, ATTENDANCE_SCORE_WEIGHT, E_LEARNING_SCORE_WEIGHT, FINAL_PRESENTATION_SCORE_WEIGHT, MIN_ATTENDANCE_THRESHOLD, MIN_E_LEARNING_COMPLETION_THRESHOLD, MIN_SURVEY_SCORE_THRESHOLD, ROADMAP_SCORE_WEIGHT, SURVEY_SCORE_WEIGHT, WORKBOOK_SCORE_WEIGHT } from '@interfaces';
import { AcceleratorAreaService } from '../../../accelerator-area.service';
import { AccDeliveryService } from '../../acc-delivery.service';
import { getSetup } from './grid/get-setup';

interface Scores {
	eLearningScore: number,
	minELearningScore: number,
	attendanceScore: number,
	minAttendanceScore: number,
	surveyScore: number,
	minSurveyScore: number,
}
export interface Graduation extends Scores {
	accVentureId: number,
	ventureName: string,
	entrepreneurs: string,
	status: string,
	totalScore: number,
	issues: string | undefined,
	workbookScore: number | undefined,
	roadmapScore: number | undefined,
	finalPresentationScore: number | undefined,
	updatedUTC: number,
}

@Component({
	selector: 'acc-graduation-view-part',
	standalone: true,
	imports: [GridContentWidget],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './graduation-view.part.html',
})
export class AccGraduationViewPart {

	private readonly util = inject(UtilityService);
	private readonly dialogService = inject(DialogService);
	private readonly pageService = inject(AccDeliveryService);
	private readonly areaService = inject(AcceleratorAreaService);

	public pageTabsGridSize = computed<PageTabsMainTabGridSize | undefined>(() => {
		if (this.pageService.layout()) return { layout: this.pageService.layout()!, viewSelector: true };
		return undefined;
	});

	public graduationData = computed<Graduation[]>(() => {
		const data = this.areaService.data();
		if (!data) return [];

		const entrepreneursByVenture = this.util.array.toArrayMap(data.entrepreneurs, e => e.accVentureId);

		return data.ventures.map(v => {
			const entrepreneurs = entrepreneursByVenture[v.accVentureId];

			// if there are no entrepreneurs, the venture shouldn't be participating, but just in case to avoid showing ventures with no entrepreneurs
			if (!entrepreneurs || entrepreneurs.length === 0) return undefined;

			const entrepreneurWithLowAttendance: string[] = [];
			const entrepreneurWithLowSurveyCompletion: string[] = [];
			const entrepreneurWithLowELearningCompletion: string[] = [];

			// initialize scores object to keep track of total and minimum scores for the venture
			const scores = {
				eLearningScore: 0,
				minELearningScore: 100,
				attendanceScore: 0,
				minAttendanceScore: 100,
				surveyScore: 0,
				minSurveyScore: 100,
			}

			const totalEntrepreneurs = entrepreneurs?.length ?? 0;
			entrepreneurs.forEach(e => {
				const learningScore = e.eLearningScore ?? 0;
				const attendanceScore = e.attendanceScore ?? 0;
				const surveyScore = e.surveyScore ?? 0;

				//
				// calculating scores for the venture, 
				// also keeping track of minimum scores and entrepreneurs with low attendance for issue flagging
				//
				scores.eLearningScore = Math.max(scores.eLearningScore, learningScore);
				scores.attendanceScore = Math.max(scores.attendanceScore, attendanceScore);
				scores.surveyScore = Math.max(scores.surveyScore, surveyScore);

				scores.minELearningScore = Math.min(scores.minELearningScore, learningScore);
				scores.minAttendanceScore = Math.min(scores.minAttendanceScore, attendanceScore);
				scores.minSurveyScore = Math.min(scores.minSurveyScore, surveyScore);

				if (attendanceScore < MIN_ATTENDANCE_THRESHOLD) {
					entrepreneurWithLowAttendance.push(e.fullName);
				}

				if (surveyScore < MIN_SURVEY_SCORE_THRESHOLD) {
					entrepreneurWithLowSurveyCompletion.push(e.fullName);
				}

				if ((e.eLearningCompletionPercentage ?? 0) < MIN_E_LEARNING_COMPLETION_THRESHOLD) {
					entrepreneurWithLowELearningCompletion.push(e.fullName);
				}
			});

			let issues: string | undefined = undefined;
			if (v.status === 'Participating') {
				if (entrepreneurWithLowAttendance.length) {
					issues = `Attendance too low (${entrepreneurWithLowAttendance.join(', ')})`;
					// } else if (v.roadmapScore == undefined) { // check for both null & undefined, as from backend it can come as null
					// 	issues = 'Missing Roadmap Score';
				}
				// else if (entrepreneurWithLowSurveyCompletion.length) {
				// 	issues = `Survey Completion too low (${entrepreneurWithLowSurveyCompletion.join(', ')})`;
				// }
				// else if (entrepreneurWithLowELearningCompletion.length) {
				// 	issues = `eLearning Completion too low (${entrepreneurWithLowELearningCompletion.join(', ')})`;
				// }
				else if (v.finalPresentationScore === undefined) {
					issues = 'Missing Final Presentation Score';
				}
				else if (v.finalPresentationScore === 0) {
					issues = 'Final Presentation not given';
				}
				// else if (v.workbookScore == undefined) {
				// 	issues = 'Missing Workbook Score';
				// }
			}


			return {
				accVentureId: v.accVentureId,
				ventureName: v.ventureName,
				entrepreneurs: entrepreneurs ? entrepreneurs.map(e => e.fullName).join(', ') : '',
				status: v.status === 'Participating' ? 'Undecided' : v.status,
				totalScore: this.calculateTotalScore(scores, v),
				eLearningScore: Math.round(scores.eLearningScore),
				minELearningScore: Math.round(scores.minELearningScore),
				attendanceScore: Math.round(scores.attendanceScore),
				minAttendanceScore: Math.round(scores.minAttendanceScore),
				surveyScore: Math.round(scores.surveyScore),
				minSurveyScore: Math.round(scores.minSurveyScore),
				issues,
				workbookScore: v.workbookScore,
				roadmapScore: v.roadmapScore,
				finalPresentationScore: v.finalPresentationScore,
				updatedUTC: v.updatedUTC
			}
		}).filter(v => !!v)
	});

	private calculateTotalScore(scores: Scores, venture: AccVentureFuncData): number {
		const roadmap = (venture.roadmapScore ?? 0) * ROADMAP_SCORE_WEIGHT;
		const presentation = (venture.finalPresentationScore ?? 0) * FINAL_PRESENTATION_SCORE_WEIGHT;
		const workbook = (venture.workbookScore ?? 0) * WORKBOOK_SCORE_WEIGHT;

		const avgELearning = scores.eLearningScore * E_LEARNING_SCORE_WEIGHT;
		const avgSurvey = scores.surveyScore * SURVEY_SCORE_WEIGHT;
		const avgAttendance = scores.attendanceScore * ATTENDANCE_SCORE_WEIGHT;

		return Math.round(avgELearning + avgSurvey + avgAttendance + roadmap + presentation + workbook);
	}

	public canVenturesBeGraduated = computed(() => {
		return this.graduationData().every(v => !v.issues);
	});

	public setup = getSetup(this.util, this.dialogService, this.areaService, this.canVenturesBeGraduated);


}